// Custody section — the wallet system on main's model.
//
// One tab, seven sub-screens — rather than seven more entries in a mobile tab
// bar that already holds five. Grouping by job keeps an operator paying out a
// trade inside one section instead of hunting across a flat list.
//
// Flow leads (17 Oct 2026): the operator's seven steps, start to end, with
// live status and the button for each. Approvals carries a live count: those
// cards have hard expiry windows, so burying them would mean walking to a
// device for a transaction that already lapsed.
//
// On the design system (docs/DESIGN-SYSTEM.md, 22 Oct 2026): sizes are roles.

import { CustodySigninShell } from '../../components/v2/custody/SigninLine';
import { NavLink, Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { custody as api } from '../../lib/api';
import { Flow } from './Flow';
import { Approvals } from './Approvals';
import { Balances } from './Balances';
import { Deposits } from './Deposits';
import { Addresses } from './Addresses';
import { Whitelist } from './Whitelist';
import { Keys } from './Keys';
import { MoneyV2 } from '../v2/custody/Money';
import { uiV2 } from '../../lib/ui-v2';

const subTabs = [
  { to: 'flow',      label: 'Flow' },
  { to: 'approvals', label: 'Approvals' },
  { to: 'balances',  label: 'Balances' },
  { to: 'deposits',  label: 'Deposits' },
  { to: 'addresses', label: 'Addresses' },
  { to: 'whitelist', label: 'Payout addresses' },
  { to: 'keys',      label: 'Devices & keys' },
];

export function CustodySection() {
  // THE REBUILT CONSOLE (batch 5): the Wallets money screen is the landing and what /custody/balances answers — every "Open Wallets" button in
  // Telegram lands there; the other anchors (approvals, deposits, keys, whitelist) stay as they are.
  const money = uiV2(5);
  const tabs = money ? [{ to: 'balances', label: 'Wallets' }, ...subTabs.filter((t) => t.to !== 'balances')] : subTabs;
  // Same query key as the Approvals screen, so TanStack dedupes the request.
  const approvals = useQuery({
    queryKey: ['custody', 'approvals'],
    queryFn: api.approvals,
    refetchInterval: 15_000,
  });
  const pending = approvals.data?.approvals.length ?? 0;

  return (
    // The lapsed custody sign-in: one line above the tabs (his "why it all goes red everytime", 2 Nov 2026).
    <CustodySigninShell>
    <div className="space-y-3 lg:space-y-4">
      <nav className="flex gap-0.5 overflow-x-auto rounded-lg bg-tg-secondary p-1 lg:gap-1 lg:bg-panel-2">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              `flex min-h-[44px] shrink-0 lg:min-h-[40px] items-center gap-1 rounded-md px-3 text-body font-medium transition-colors lg:gap-1.5 ${
                isActive
                  ? 'bg-tg-bg text-tg-text shadow-sm lg:bg-panel lg:text-tg-text'
                  : 'text-tg-hint lg:text-tg-hint'
              }`
            }
          >
            {t.label}
            {t.to === 'approvals' && pending > 0 && (
              <span className="rounded-full bg-tg-destructive px-1.5 py-px text-label font-bold text-white">
                {pending}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<Navigate to={money ? 'balances' : 'flow'} replace />} />
        <Route path="flow"      element={<Flow />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="balances"  element={money ? <MoneyV2 /> : <Balances />} />
        <Route path="deposits"  element={<Deposits />} />
        <Route path="addresses" element={<Addresses />} />
        <Route path="whitelist" element={<Whitelist />} />
        <Route path="keys"      element={<Keys />} />
      </Routes>
    </div>
    </CustodySigninShell>
  );
}
